import Phaser from "phaser";
import { InteractableTree } from "@/world/objects/InteractableTree";
import { Fire, randomFirePosition } from "@/world/objects/Fire";
import { playWateringAnimation } from "@/world/objects/WateringCan";
import type { ScreenPos } from "@/world/map/Iso";
import { playWaterSplash, playTreeGrow, playFireExtinguish } from "@/audio/SoundManager";

const INTERACT_RANGE = 1.8; // tiles
const MILESTONE_STORAGE_KEY = "mythglade_milestones";

interface InteractTarget {
  kind: "tree" | "fire";
  tree?: InteractableTree;
  fire?: Fire;
  dist: number;
  worldX: number;
  worldY: number;
  screenX: number;
  screenY: number;
  displayHeight: number;
}

export type MilestoneCallback = (tag: string) => void;

export class InteractionSystem {
  private eKey: Phaser.Input.Keyboard.Key;
  private trees: InteractableTree[] = [];
  private fire: Fire | null = null;
  private origin: ScreenPos;
  private scene: Phaser.Scene;
  private busy = false;
  private prompt?: Phaser.GameObjects.Text;
  private heightMap: number[][] = [];
  private milestones: Record<string, number>;
  private onMilestone: MilestoneCallback | null = null;

  constructor(scene: Phaser.Scene, origin: ScreenPos) {
    this.scene = scene;
    this.origin = origin;
    this.eKey = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.E);
    this.milestones = this.loadMilestones();
  }

  setMilestoneHandler(fn: MilestoneCallback): void {
    this.onMilestone = fn;
  }

  setTrees(trees: InteractableTree[]): void {
    this.trees = trees;
  }

  setHeightMap(heightMap: number[][]): void {
    this.heightMap = heightMap;
  }

  /** Spawns a fire at a random position, avoiding trees and previous fire location. */
  spawnFire(): void {
    const exclude: Array<{ worldX: number; worldY: number }> = this.trees.map((t) => ({
      worldX: t.worldX,
      worldY: t.worldY,
    }));
    if (this.fire?.isAlive) {
      exclude.push({ worldX: this.fire.worldX, worldY: this.fire.worldY });
    }
    const { tx, ty } = randomFirePosition(exclude);
    const elev = this.heightMap[ty]?.[tx] ?? 0;
    this.fire = new Fire(this.scene, this.origin, tx, ty, elev);
  }

  /**
   * Call every frame. Pass current player world position and whether chat is active.
   */
  update(playerX: number, playerY: number, chatActive: boolean): void {
    if (chatActive) {
      this.hidePrompt();
      return;
    }

    const target = this.findNearest(playerX, playerY);

    if (target) {
      const label = target.kind === "fire" ? "[E] Extinguish" : "[E] Water";
      this.showPrompt(target, label);
    } else {
      this.hidePrompt();
    }

    if (Phaser.Input.Keyboard.JustDown(this.eKey) && !chatActive && !this.busy && target) {
      if (target.kind === "tree" && target.tree && !target.tree.isMaxGrowth && !target.tree.isGrowing) {
        this.waterTree(target.tree);
      } else if (target.kind === "fire" && target.fire?.isAlive) {
        this.extinguishFire(target.fire);
      }
    }
  }

  private waterTree(tree: InteractableTree): void {
    this.busy = true;
    this.hidePrompt();
    playWaterSplash();
    playWateringAnimation(this.scene, this.origin, tree.worldX, tree.worldY, () => {
      tree.grow(this.scene);
      playTreeGrow();
      this.busy = false;

      if (tree.isMaxGrowth) {
        tree.addFruit(this.scene);
        this.incrementMilestone("trees_maxed");
        if (this.milestones["trees_maxed"] === 3) {
          this.onMilestone?.("master_gardener");
        }
      }
    });
  }

  private extinguishFire(fire: Fire): void {
    this.busy = true;
    this.hidePrompt();
    playWaterSplash();
    playWateringAnimation(this.scene, this.origin, fire.worldX, fire.worldY, () => {
      playFireExtinguish();
      fire.extinguish(this.scene, () => {
        // Spawn a new fire after the old one is gone
        this.spawnFire();
      });
      this.busy = false;

      this.incrementMilestone("fires_extinguished");
      if (this.milestones["fires_extinguished"] === 3) {
        this.onMilestone?.("fire_extinguisher");
      }
    });
  }

  private findNearest(px: number, py: number): InteractTarget | null {
    let best: InteractTarget | null = null;
    let bestDist = INTERACT_RANGE;

    // Check trees
    for (const tree of this.trees) {
      if (tree.isMaxGrowth) continue;
      const d = tree.distanceTo(px, py);
      if (d < bestDist) {
        bestDist = d;
        best = {
          kind: "tree",
          tree,
          dist: d,
          worldX: tree.worldX,
          worldY: tree.worldY,
          screenX: tree.image.x,
          screenY: tree.image.y,
          displayHeight: tree.image.displayHeight,
        };
      }
    }

    // Check fire
    if (this.fire?.isAlive) {
      const d = this.fire.distanceTo(px, py);
      if (d < bestDist) {
        best = {
          kind: "fire",
          fire: this.fire,
          dist: d,
          worldX: this.fire.worldX,
          worldY: this.fire.worldY,
          screenX: this.fire.screenX,
          screenY: this.fire.screenY,
          displayHeight: 20,
        };
      }
    }

    return best;
  }

  private showPrompt(target: InteractTarget, label: string): void {
    if (!this.prompt) {
      this.prompt = this.scene.add.text(0, 0, "", {
        fontSize: "11px",
        color: "#ffffff",
        backgroundColor: "rgba(0,0,0,0.6)",
        padding: { left: 4, right: 4, top: 2, bottom: 2 },
      });
      this.prompt.setOrigin(0.5, 1);
      this.prompt.setDepth(999999);
    }
    this.prompt.setText(label);
    this.prompt.setPosition(target.screenX, target.screenY - target.displayHeight - 4);
    this.prompt.setVisible(true);
  }

  private hidePrompt(): void {
    this.prompt?.setVisible(false);
  }

  destroy(): void {
    this.prompt?.destroy();
    this.fire?.destroy();
  }

  private incrementMilestone(key: string): void {
    this.milestones[key] = (this.milestones[key] ?? 0) + 1;
    this.saveMilestones();
  }

  private loadMilestones(): Record<string, number> {
    try {
      const raw = localStorage.getItem(MILESTONE_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  }

  private saveMilestones(): void {
    localStorage.setItem(MILESTONE_STORAGE_KEY, JSON.stringify(this.milestones));
  }
}
